<template>
  <div class="custom-fields-builder">
    <div>
      <label class="form-label cf-title">Custom fields <span class="optional-text">(Optional)</span></label>
      <p class="help-text">Collect extra information from people when they reply to your request.</p>
    </div>

    <FormKit type="list" name="customFields">
      <div v-if="fields.length" class="cf-list">
        <FormKit v-for="(field, index) in fields" :key="index" type="group">
          <div class="cf-row" data-testid="custom-field-row">
            <div class="cf-row-main">
              <FormKit
                type="text"
                name="label"
                placeholder="Field label (e.g. Department)"
                :model-value="field.label.custom"
                maxlength="200"
                validation="required:trim"
                validation-visibility="live"
                :validation-messages="{ required: 'Add a label for this field.' }"
                :sections-schema="errorSections"
                data-testid="custom-field-label"
                outer-class="!mb-0 flex-1"
                @input="(value: string) => updateLabel(index, value)"
              />

              <FormKit
                type="select"
                name="type"
                :options="fieldTypeOptions"
                :model-value="field.type"
                data-testid="custom-field-type"
                outer-class="!mb-0 w-36"
                @input="(type: CustomFieldType) => updateType(index, type)"
              />

              <button type="button" class="cf-icon-button" title="Remove field" data-testid="custom-field-remove" @click="removeField(index)">
                <Icon name="general.trash" size="sm" />
              </button>
            </div>

            <FormKit
              type="checkbox"
              name="required"
              label="Required"
              :model-value="!field.optional"
              data-testid="custom-field-required"
              outer-class="!mb-0"
              @input="(required: boolean) => updateRequired(index, required)"
            />

            <FormKit v-if="field.type === 'dropdown'" type="list" name="options">
              <div class="cf-options">
                <div v-for="(option, optionIndex) in field.dropdown?.options || []" :key="optionIndex" class="cf-option-row">
                  <FormKit
                    type="text"
                    placeholder="Option label"
                    :model-value="option.label"
                    maxlength="200"
                    validation="required:trim"
                    validation-visibility="live"
                    :validation-messages="{ required: 'Add a label for this option.' }"
                    :sections-schema="errorSections"
                    data-testid="custom-field-option"
                    outer-class="!mb-0 flex-1"
                    @input="(value: string) => updateOption(index, optionIndex, value)"
                  />
                  <button
                    type="button"
                    class="cf-icon-button"
                    title="Remove option"
                    :disabled="(field.dropdown?.options.length || 0) <= 1"
                    data-testid="custom-field-option-remove"
                    @click="removeOption(index, optionIndex)"
                  >
                    <Icon name="general.close" size="sm" />
                  </button>
                </div>
                <Button type="link" variant="blue" text="Add option" icon="general.plus" data-testid="custom-field-add-option" @click="addOption(index)" />
              </div>
            </FormKit>
          </div>
        </FormKit>
      </div>
    </FormKit>

    <Button
      v-if="fields.length < MAX_CUSTOM_FIELDS"
      type="ghost"
      variant="blue"
      text="Add custom field"
      icon="general.plus"
      data-testid="add-custom-field"
      @click="addField"
    />
    <p v-else class="help-text">You can add up to {{ MAX_CUSTOM_FIELDS }} custom fields.</p>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { FormKit } from '@formkit/vue';
import { Button, Icon } from '@/storybook';
import type { CustomField, CustomFieldType } from '../../types';

// Matches RequestContent::MAX_CUSTOM_FIELDS on the backend.
const MAX_CUSTOM_FIELDS = 10;

const fieldTypeOptions: Array<{ label: string; value: CustomFieldType }> = [
  { label: 'Short text', value: 'text' },
  { label: 'Number', value: 'numeric' },
  { label: 'Email', value: 'email' },
  { label: 'Dropdown', value: 'dropdown' },
  { label: 'Checkbox', value: 'checkbox' },
];

interface Props {
  modelValue?: CustomField[];
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
});

const emit = defineEmits<{ 'update:modelValue': [value: CustomField[]] }>();

const cloneFields = (value: CustomField[]): CustomField[] => {
  return JSON.parse(JSON.stringify(value || []));
};

const fields = ref<CustomField[]>(cloneFields(props.modelValue));

// Saved keys and option values are what existing replies are matched against, so they never change.
const savedKeys = new Set(fields.value.map(field => field.key).filter(Boolean));
const savedOptionValues = new Set(fields.value.flatMap(field => (field.dropdown?.options || []).map(option => `${field.key}:${option.value}`)));

let lastEmitted = '';

watch(
  () => props.modelValue,
  newValue => {
    const incoming = JSON.stringify(newValue || []);
    if (incoming !== lastEmitted && incoming !== JSON.stringify(fields.value)) {
      fields.value = cloneFields(newValue || []);
    }
  }
);

const slugify = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

const uniqueSlug = (base: string, takenKeys: string[]): string => {
  let candidate = base;
  let suffix = 2;
  while (takenKeys.includes(candidate)) {
    candidate = `${base}_${suffix}`;
    suffix += 1;
  }
  return candidate;
};

const errorSections = { message: { attrs: { 'data-testid': 'custom-field-error' } } };

// The server stores the ticket ID under custom_id, so fields cannot use it.
const RESERVED_KEYS = ['custom_id'];

const buildOptions = (field: CustomField, key: string) => {
  const takenValues: string[] = [];
  return (field.dropdown?.options || [])
    .filter(option => option.label.trim())
    .map((option, optionIndex) => {
      const value = savedOptionValues.has(`${key}:${option.value}`)
        ? option.value
        : uniqueSlug(slugify(option.label) || `option_${optionIndex + 1}`, takenValues);
      takenValues.push(value);
      return { label: option.label, value };
    });
};

const emitFields = () => {
  const takenKeys: string[] = [...RESERVED_KEYS, ...savedKeys];
  const withKeys = fields.value.map((field, index) => {
    const key = savedKeys.has(field.key) ? field.key : uniqueSlug(slugify(field.label.custom) || `field_${index + 1}`, takenKeys);
    takenKeys.push(key);
    if (!field.dropdown) {
      return { ...field, key };
    }
    return { ...field, key, dropdown: { ...field.dropdown, options: buildOptions(field, key) } };
  });
  lastEmitted = JSON.stringify(withKeys);
  emit('update:modelValue', withKeys);
};

const addField = () => {
  fields.value.push({
    key: '',
    type: 'text',
    label: { type: 'custom', custom: '' },
    optional: true,
  });
  emitFields();
};

const removeField = (index: number) => {
  fields.value.splice(index, 1);
  emitFields();
};

const updateLabel = (index: number, value: string) => {
  fields.value[index].label.custom = value;
  emitFields();
};

const updateRequired = (index: number, required: boolean) => {
  fields.value[index].optional = !required;
  emitFields();
};

const updateType = (index: number, type: CustomFieldType) => {
  const field = fields.value[index];
  field.type = type;
  if (type === 'dropdown' && !field.dropdown) {
    field.dropdown = { defaultValue: null, options: [{ label: '', value: '' }] };
  }
  emitFields();
};

const updateOption = (index: number, optionIndex: number, value: string) => {
  const field = fields.value[index];
  if (!field.dropdown) {
    return;
  }
  field.dropdown.options[optionIndex] = { ...field.dropdown.options[optionIndex], label: value };
  emitFields();
};

const addOption = (index: number) => {
  const field = fields.value[index];
  if (!field.dropdown) {
    field.dropdown = { defaultValue: null, options: [] };
  }
  field.dropdown.options.push({ label: '', value: '' });
  emitFields();
};

const removeOption = (index: number, optionIndex: number) => {
  const field = fields.value[index];
  if (!field.dropdown || field.dropdown.options.length <= 1) {
    return;
  }
  field.dropdown.options.splice(optionIndex, 1);
  emitFields();
};
</script>

<style scoped>
.custom-fields-builder {
  @apply flex flex-col gap-3 pt-6 border-t border-zinc-200 dark:border-zinc-700;
}

.cf-title {
  @apply mb-1;
}

.form-label {
  @apply block text-sm font-medium text-zinc-700 dark:text-zinc-300;
}

.help-text {
  @apply text-xs text-zinc-500 dark:text-zinc-400;
}

.optional-text {
  @apply text-zinc-500 dark:text-zinc-400 font-normal;
}

.cf-list {
  @apply flex flex-col gap-3;
}

.cf-row {
  @apply flex flex-col gap-2 p-3 rounded-10 ring-1 ring-slate-300 dark:ring-zinc-700 bg-white dark:bg-zinc-800;
}

.cf-row-main {
  @apply flex items-start gap-2;
}

.cf-icon-button {
  @apply flex items-center justify-center p-2 rounded-10 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed;
}

.cf-options {
  @apply flex flex-col gap-2 pl-2 border-l-2 border-zinc-200 dark:border-zinc-700;
}

.cf-option-row {
  @apply flex items-start gap-2;
}
</style>
